import moment from 'moment'
import _ from 'lodash'
import { callAuthorizedGraphQLEndpoint } from 'Module/graphQL'
import { isValidDate, getUNIXTimestamp } from './../Date'
import { isExternalDistributorCampaign } from './../Campaigns'
import {
  CONVERSION_REPORT_TYPE,
  CONVERSION_LOG_STATUS,
} from '../../shared/constants'
import { CONVERSIONS_FETCH_STEP } from '../../constants'

let csvHeaders, headersLen
let datas,
  groupedConversionsMap,
  flyerCodes,
  metricsInFile,
  updatedConversionsMap,
  totalConversionsCount
const flyerCodeMap = {}

export const SHOW_ERROR_NUMBER = 20

export const WRONG_FILE_STRUCTURE = 'WRONG_FILE_STRUCTURE'
export const MISSING_VALUE = 'MISSING_VALUE'
export const NOT_EXIST_METRIC = 'NOT_EXIST_METRIC'
export const DUPLICATE_FLYER_CODE = 'DUPLICATE_FLYER_CODE'
export const NOT_EXIST_FLYER_CODE = 'NOT_EXIST_FLYER_CODE'
export const NOT_COMPLETED_MISSION_FLYER_CODE =
  'NOT_COMPLETED_MISSION_FLYER_CODE'
export const WRONG_CONVERSION_ORDER = 'WRONG_CONVERSION_ORDER'
export const LESS_CONVERSIONS_THAN_DB = 'LESS_CONVERSIONS_THAN_DB'
export const INVALID_TIMESTAMP = 'INVALID_TIMESTAMP'
export const NO_NEW_CONVERSION = 'NO_NEW_CONVERSION'
export const LOWER_FLYER_THAN_SYSTEM =
  'Some flyer codes in the file have a lower number of conversions compared to what the flyer codes already have in our system.'

const ERROR_CSV_FILE_NAME = 'error_report.csv'

export const getCampaignConversions = `
  query (
    $campaignId: ID!,
    $first: Int,
    $after: String,
    $skip: Int
  ) {
    campaignConversions (
      campaignId: $campaignId,
      first: $first,
      after: $after,
      skip: $skip
    ) {
      edges {
          cursor,
          node {
              flyerCode,
              metricName,
              count
          }
      },
      totalCount,
      pageInfo {
          endCursor,
          hasNextPage
      }
    }
  }
`
export const getCampaignFlyercodes = `
  query (
    $campaignId: ID!,
    $first: Int,
    $after: String,
    $skip: Int
  ) {
    campaignFlyercodes (
      campaignId: $campaignId,
      first: $first,
      after: $after,
      skip: $skip
    ) {
      edges {
          cursor,
          node {
              flyerCode,
              missionStatus
          }
      },
      totalCount,
      pageInfo {
          endCursor,
          hasNextPage
      }
    }
  }
`

export const getConversionSampleData = `
  query ($campaignId: ID!) {
    conversionSampleData(campaignId: $campaignId) {
      row,
      flyerCode,
      metricName,
      timestamp
    }
  }
`

export const uploadConversionLog = `
  mutation ($input: UploadConversionLogInput) {
    uploadConversionLog (input: $input) {
      id
    }
  }
`

export const uploadConversions = `
  mutation ($input: UploadConversionsInput) {
    uploadConversions (input: $input) {
      success
    }
  }
`

const removeQuotes = str => {
  if (str.endsWith('"') && str.startsWith('"')) return str.slice(1, -1)
  return str
}

const normaliseString = str => {
  return removeQuotes(str.trim())
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '')
}

export const uniqueElements = arr => [...new Set(arr)]

export const duplicatedElements = arr => {
  const { result } = arr.reduce(
    (acc, item) => {
      if (!acc.m.get(item)) {
        acc.m.set(item, 1)
      } else if (acc.m.get(item) === 1) {
        acc.m.set(item, 2)
        acc.result.push(item)
      }
      return acc
    },
    { m: new Map(), result: [] },
  )

  return result
}

export const validateMetrics = (metrics, metricsInFile) =>
  metricsInFile.reduce((acc, metric) => {
    if (!metrics.includes(metric)) {
      acc.push(metric)
    }
    return acc
  }, [])

export const validateFileStructure = (csvHeaders, metrics, withTimestamp) => {
  const duplicatedHeaders = duplicatedElements(csvHeaders)
  if (duplicatedHeaders.length) return false

  const headersLength = csvHeaders.length

  if (withTimestamp) {
    const validHeaders = ['flyercode', 'metric', 'timestamp']
    if (headersLength !== 3) return false
    return csvHeaders.reduce((acc, header, index) => {
      if (normaliseString(header) !== validHeaders[index]) acc = false
      return acc
    }, true)
  }
  if (!headersLength || normaliseString(csvHeaders[0]) !== 'flyercode')
    return false

  if (metrics.length !== headersLength - 1) {
    return false
  }
  for (let index = 0; index < metrics.length; index++) {
    if (metrics[index] !== csvHeaders[index + 1]) {
      return false
    }
  }
  return true
}

const validateFlyerCodesDuplicates = flyerCodes =>
  duplicatedElements(flyerCodes)

export const validateFlyerCodes = async (
  campaignId,
  groupedConversionsMap,
  flyerCodes,
) => {
  const m = new Map()
  const missingFlyerCodes = []
  const flyerCodesWithLessConversions = []
  const updatedConversionsMap = { ...groupedConversionsMap }
  let skip = 0
  while (true) {
    const response = await callAuthorizedGraphQLEndpoint(
      getCampaignConversions,
      {
        campaignId,
        first: CONVERSIONS_FETCH_STEP,
        skip,
      },
    )
    const result = (await response.json()).data.campaignConversions
    const {
      edges,
      pageInfo: { hasNextPage },
    } = result

    edges.forEach(({ node: { flyerCode, metricName, count } }) => {
      const diff = (groupedConversionsMap[flyerCode + metricName] | 0) - count
      if (diff < 0) flyerCodesWithLessConversions.push(flyerCode)
      updatedConversionsMap[flyerCode + metricName] = diff
      m.set(flyerCode, 1)
    })
    if (!hasNextPage) break
    skip += CONVERSIONS_FETCH_STEP
  }
  flyerCodes.forEach(flyerCode => {
    if (!m.get(flyerCode)) missingFlyerCodes.push(flyerCode)
  })
  return {
    missingFlyerCodes: uniqueElements(missingFlyerCodes),
    flyerCodesWithLessConversions: uniqueElements(
      flyerCodesWithLessConversions,
    ),
    updatedConversionsMap,
  }
}

export const validateConversionsOrder = async (
  campaignId,
  datas,
  isUSFormat = false,
) => {
  /**
   * {
   *  row: row number
   *  misMatched: cols which mismatch each other
   * }
   */
  const compareOrderResult = []
  const response = await callAuthorizedGraphQLEndpoint(
    getConversionSampleData,
    {
      campaignId,
    },
  )
  const randomConversions = (await response.json()).data.conversionSampleData
  randomConversions.forEach(
    ({ row, flyerCode, metricName, timestamp: dbDate }) => {
      const csvRow = datas[row] || ['', '', '']
      const compareResult = {
        row,
        misMatched: [],
      }

      const dateWoTime = dbDate.split('T')[0]
      const time = dbDate.split('T')[1].split('.')[0]
      const [year, month, day] = dateWoTime.split('-')
      const timestamp = isUSFormat
        ? `${month}/${day}/${year} ${time}`
        : `${day}/${month}/${year} ${time}`

      if (flyerCode !== csvRow[0]) compareResult.misMatched.push(0)
      if (metricName !== csvRow[1]) compareResult.misMatched.push(1)
      const dbTimestamp = isValidDate(timestamp, isUSFormat)
        ? getUNIXTimestamp(timestamp, isUSFormat)
        : null

      const fileTimestamp = isValidDate(csvRow[2], isUSFormat)
        ? getUNIXTimestamp(csvRow[2], isUSFormat)
        : null

      if (!dbTimestamp || !fileTimestamp || dbTimestamp !== fileTimestamp)
        compareResult.misMatched.push(2)

      if (compareResult.misMatched.length) {
        compareOrderResult.push(compareResult)
      }
    },
  )

  return { compareOrderResult, randomConversions }
}

export const validateTimestamps = (datas, isUSFormat = false) => {
  const invalidRows = []

  datas.forEach((csvRow, row) => {
    if (!isValidDate(csvRow[2], isUSFormat)) invalidRows.push(row)
  })

  return { invalidRows }
}

export const extractCorrectHeaders = (headers, standardLen) => {
  const csvHeadersTemp = headers.split('\n')[0].split(',')
  const csvHeaders = []
  for (let index = 0; index < csvHeadersTemp.length; index++) {
    const element = csvHeadersTemp[index]
    csvHeaders.push(element.match(/(?:"[^"]*"|^[^"]*$)/)[0].replace(/"/g, ''))
  }
  /**
   * extract the exact header due to the file may consist example part and soon
   */
  let headersLen = csvHeaders.length
  while (headersLen > standardLen) {
    headersLen--
    csvHeaders.pop()
  }
  return { csvHeaders, headersLen }
}

export const extractInfoFromReportFile = ({
  dataArr,
  headersLen,
  conversionsCount,
  csvHeaders,
  withTimestamp,
}) => {
  /**
   * store number of conversions grouped by flyer code, metric name
   * i.e groupedConversionsMap['OOOPPP111' + 'SignUp'] = 10
   */
  const groupedConversionsMap = {}
  const metricsInFile = []
  const flyerCodes = []

  /**
   * @type { row: number, pos: [number] }
   * row: the row number which has missing or wrong value
   * pos: the cols which has the empty or wrong value
   */
  const missingValues = []
  let totalConversionsCount = 0

  const datas = dataArr.map(data => {
    const csvRow = data
      .replace(/"/g, '')
      .split('\n')[0]
      .split(',')
    return csvRow
      .slice(0, headersLen)
      .map((value, index) =>
        index ? value.trim() : value.trim().toUpperCase(),
      )
  })

  while (datas.length) {
    const lastRow = datas.pop()
    if (lastRow.findIndex(item => item) !== -1) {
      datas.push(lastRow)
      break
    }
  }
  if (withTimestamp) totalConversionsCount = datas.length

  datas.forEach((csvRow, row) => {
    const missing = []
    csvRow.forEach((value, col) => {
      if (!value && withTimestamp) {
        missing.push(col)
        return
      }

      if (!col) {
        if (!withTimestamp) {
          flyerCodes.push(value)
        } else {
          if (!flyerCodes.includes(value)) flyerCodes.push(value)
        }
      }

      if (col && !withTimestamp) {
        // csvRow[0]: flyer code, csvHeaders[col]: metric name
        const numVal = isNaN(value) ? 0 : Number(value)
        groupedConversionsMap[csvRow[0] + csvHeaders[col]] = numVal
        totalConversionsCount += numVal
      }

      if (col === 1 && withTimestamp) {
        if (!metricsInFile.includes(value)) metricsInFile.push(value)
        if (row < conversionsCount) {
          // csvRow[0]: flyer code, value: metric name
          const key = csvRow[0] + value
          groupedConversionsMap[key] = (groupedConversionsMap[key] || 0) + 1
        }
      }
    })

    if (missing.length) {
      missingValues.push({
        row,
        pos: missing,
      })
    }
  })

  return {
    datas,
    groupedConversionsMap,
    metricsInFile,
    flyerCodes,
    missingValues,
    totalConversionsCount,
  }
}

export const validateFlyerCodesBelongToCampaign = async (
  campaignId,
  flyerCodes,
) => {
  const missingFlyerCodes = []

  let skip = 0
  while (true) {
    const response = await callAuthorizedGraphQLEndpoint(
      getCampaignFlyercodes,
      {
        campaignId,
        first: 200,
        skip,
      },
    )
    const result = (await response.json()).data.campaignFlyercodes
    const {
      edges,
      pageInfo: { hasNextPage },
    } = result

    edges.forEach(({ node: { flyerCode, missionStatus } }) => {
      if (missionStatus && missionStatus >= 35) {
        flyerCodeMap[flyerCode] = 2
      } else {
        flyerCodeMap[flyerCode] = 1
      }
    })
    if (!hasNextPage) break
    skip += 200
  }
  flyerCodes.forEach(flyerCode => {
    if (!flyerCodeMap[flyerCode]) missingFlyerCodes.push(flyerCode)
  })
  return missingFlyerCodes
}

export const validateFlyerCodesBelongToCompletedMission = flyerCodes => {
  const missingFlyerCodes = []
  flyerCodes.forEach(flyerCode => {
    if (flyerCodeMap[flyerCode] !== 2) missingFlyerCodes.push(flyerCode)
  })
  return missingFlyerCodes
}

export const validateConversionsCount = async campaignId => {
  const flyerCodesWithLessConversions = []
  const flyerCodesInDBNotInFile = []
  updatedConversionsMap = { ...groupedConversionsMap }
  let skip = 0
  const step = 20000

  const conversionsNodes = []
  while (true) {
    const response = await callAuthorizedGraphQLEndpoint(
      getCampaignConversions,
      {
        campaignId,
        first: step,
        skip,
      },
    )
    const result = (await response.json()).data.campaignConversions
    const {
      edges,
      pageInfo: { hasNextPage },
    } = result
    edges.forEach(({ node }) => {
      if (node) return conversionsNodes.push(node)
    })

    if (!hasNextPage) break
    skip += step
  }
  const formatedConversionsResult = conversionsNodes.reduce((acc, res) => {
    const key = `${res.flyerCode}${res.metricName}`
    if (acc[key]) {
      acc[key] = {
        ...res,
        count: acc[key].count + res.count,
      }
    } else {
      acc[key] = res
    }
    return acc
  }, {})

  Object.values(formatedConversionsResult).forEach(
    ({ flyerCode, metricName, count }) => {
      const diff = (groupedConversionsMap[flyerCode + metricName] | 0) - count
      if (
        groupedConversionsMap[flyerCode + metricName] === null ||
        groupedConversionsMap[flyerCode + metricName] === undefined
      ) {
        flyerCodesInDBNotInFile.push(flyerCode)
      }
      if (diff < 0) flyerCodesWithLessConversions.push(flyerCode)
      updatedConversionsMap[flyerCode + metricName] = diff
    },
  )
  const tempMissingFlyerCodes = uniqueElements(flyerCodesInDBNotInFile)
  tempMissingFlyerCodes.forEach(missingFlyerCode => {
    flyerCodes.push(missingFlyerCode)
  })
  return {
    flyerCodesWithLessConversions: uniqueElements(
      flyerCodesWithLessConversions,
    ),
  }
}

const removeBadFlyercode = missingFlyerCodes => {
  if (missingFlyerCodes.length) {
    datas = datas.filter(item => !missingFlyerCodes.includes(item[0]))
    flyerCodes = flyerCodes.filter(item => !missingFlyerCodes.includes(item))
  }
  return
}

const treatDuplicatedFlyerCodes = duplicatedFlyerCodes => {
  const tempDuplicatesData = {}

  datas = datas.filter(data => {
    const flyerCode = data[0]
    if (duplicatedFlyerCodes.includes(flyerCode)) {
      if (!tempDuplicatesData[flyerCode]) {
        tempDuplicatesData[flyerCode] = []
      }
      tempDuplicatesData[flyerCode].push(data)
      return false
    }
    return true
  })

  Object.entries(tempDuplicatesData).forEach(([flyerCode, tempData]) => {
    const flyerCodeData = [flyerCode]
    const columnCount = tempData[0].length

    for (let i = 1; i < columnCount; i++) {
      const maxValue = _.maxBy(tempData, item => Number(item[i]))[i]
      flyerCodeData.push(maxValue)
    }

    datas.push(flyerCodeData)
  })

  flyerCodes = uniqueElements(flyerCodes)
}

const treatLessConversionFlyerCodes = () => {
  Object.values(updatedConversionsMap).forEach(value => {
    if (value < 0) {
      totalConversionsCount = totalConversionsCount - value
      value = 0
    }
  })
}

export const validateRule = async ({
  stage,
  campaignId,
  campaignType,
  reportFile,
  callbacks,
  withTimestamp,
  metrics,
  conversionsCount,
  isUSFormat,
  startRunningValidation,
}) => {
  const controller = new AbortController()
  const onCancelUpload = () => {
    controller.abort()
    callbacks.stopLoading()
  }
  switch (stage) {
    case 0:
      const reader = new FileReader()
      reader.readAsDataURL(reportFile)
      reader.addEventListener('load', async function() {
        const divider = /\r\n|\n/
        const data = Buffer.from(this.result.split(',')[1], 'base64').toString(
          'ascii',
        )
        const dataArr = data.split(divider)
        const headers = dataArr.shift()
        let standardLen = 3
        if (!withTimestamp) standardLen = metrics.length + 1
        const HeaderData = extractCorrectHeaders(headers, standardLen)
        csvHeaders = HeaderData.csvHeaders
        headersLen = HeaderData.headersLen

        if (!validateFileStructure(csvHeaders, metrics, withTimestamp)) {
          controller.abort()
          callbacks.onError(WRONG_FILE_STRUCTURE, null, null, {
            metrics,
            fileHeader: csvHeaders,
            fileData: dataArr.slice(0, 3),
          })
          return
        }
        const extractedInfo = extractInfoFromReportFile({
          dataArr,
          headersLen,
          conversionsCount,
          csvHeaders,
          withTimestamp,
        })
        datas = extractedInfo.datas
        metricsInFile = extractedInfo.metricsInFile
        flyerCodes = extractedInfo.flyerCodes
        const missingValues = extractedInfo.missingValues

        if (missingValues.length) {
          controller.abort()
          const tableData = missingValues.map(({ row }) => [row, ...datas[row]])
          callbacks.onError(MISSING_VALUE, null, null, {
            missingValues,
            csvHeaders,
            tableData,
          })
          return
        }
        if (withTimestamp) {
          const nonExistMetrics = validateMetrics(metrics, metricsInFile)
          if (nonExistMetrics.length) {
            controller.abort()
            const tableData = datas.reduce((acc, data, index) => {
              if (!metrics.includes(data[1])) acc.push([index, ...data])
              return acc
            }, [])
            callbacks.onError(NOT_EXIST_METRIC, null, null, {
              csvHeaders,
              tableData,
            })
            return
          }
        }

        const notExistFlyercodesInCampaign = await validateFlyerCodesBelongToCampaign(
          campaignId,
          flyerCodes,
        )
        const onContinueUpload = () => {
          removeBadFlyercode(notExistFlyercodesInCampaign)
          startRunningValidation()
          validateRule({
            stage: withTimestamp ? 3 : 1,
            campaignId,
            campaignType,
            reportFile,
            callbacks,
            withTimestamp,
            metrics,
            conversionsCount,
            isUSFormat,
            startRunningValidation,
          })
        }
        if (notExistFlyercodesInCampaign.length) {
          controller.abort()
          const tableData = datas.reduce((acc, data, index) => {
            if (notExistFlyercodesInCampaign.includes(data[0]))
              acc.push([index, ...data])
            return acc
          }, [])
          callbacks.onError(
            NOT_EXIST_FLYER_CODE,
            onContinueUpload,
            onCancelUpload,
            { csvHeaders, tableData },
            withTimestamp,
          )
          return
        }
        onContinueUpload()
      })
      break
    case 1:
      const flyerCodesNotBelongToCompletedMissions = isExternalDistributorCampaign(
        campaignType,
      )
        ? []
        : validateFlyerCodesBelongToCompletedMission(flyerCodes)
      const onContinueUpload = code => {
        if (code === 1) {
          removeBadFlyercode(flyerCodesNotBelongToCompletedMissions)
        }
        startRunningValidation()
        validateRule({
          stage: 2,
          campaignId,
          campaignType,
          reportFile,
          callbacks,
          withTimestamp,
          metrics,
          conversionsCount,
          isUSFormat,
          startRunningValidation,
        })
      }
      if (flyerCodesNotBelongToCompletedMissions.length) {
        controller.abort()
        const tableData = datas.reduce((acc, data, index) => {
          if (flyerCodesNotBelongToCompletedMissions.includes(data[0]))
            acc.push([index, ...data])
          return acc
        }, [])
        callbacks.onError(
          NOT_COMPLETED_MISSION_FLYER_CODE,
          onContinueUpload,
          onCancelUpload,
          { csvHeaders, tableData },
        )
        return
      }
      onContinueUpload(0)
      break
    case 2:
      const duplicatedFlyerCodes = validateFlyerCodesDuplicates(flyerCodes)
      const onContinueUpload2 = () => {
        treatDuplicatedFlyerCodes(duplicatedFlyerCodes)
        startRunningValidation()
        validateRule({
          stage: 3,
          campaignId,
          campaignType,
          reportFile,
          callbacks,
          withTimestamp,
          metrics,
          conversionsCount,
          isUSFormat,
          startRunningValidation,
        })
      }
      if (duplicatedFlyerCodes.length) {
        controller.abort()
        const tableData = datas.reduce((acc, data, index) => {
          if (duplicatedFlyerCodes.includes(data[0])) acc.push([index, ...data])
          return acc
        }, [])
        callbacks.onError(
          DUPLICATE_FLYER_CODE,
          onContinueUpload2,
          onCancelUpload,
          { csvHeaders, tableData },
        )
        return
      }
      onContinueUpload2()
      break
    case 3:
      totalConversionsCount = 0
      if (withTimestamp) {
        totalConversionsCount = datas.length
        const {
          compareOrderResult,
          randomConversions,
        } = await validateConversionsOrder(campaignId, datas, isUSFormat)
        if (compareOrderResult.length) {
          controller.abort()
          const tableData = compareOrderResult.map(({ row }) => {
            const conv = randomConversions.find(item => item.row === row)
            if (!conv) return []
            const { flyerCode, metricName, timestamp } = conv
            return [
              row,
              flyerCode,
              metricName,
              isUSFormat
                ? moment
                    .unix(new Date(timestamp) / 1000)
                    .utc()
                    .format('MM/DD/YYYY HH:mm:ss')
                : moment
                    .unix(new Date(timestamp) / 1000)
                    .utc()
                    .format('DD/MM/YYYY HH:mm:ss'),
              ...(datas[row] || ['', '', '']),
            ]
          })
          callbacks.onError(WRONG_CONVERSION_ORDER, null, null, {
            csvHeaders,
            tableData,
            compareOrderResult,
          })
          return
        }
      }

      groupedConversionsMap = {}
      datas.forEach((csvRow, row) => {
        csvRow.forEach((value, col) => {
          if (col && !withTimestamp) {
            // csvRow[0]: flyer code, csvHeaders[col]: metric name
            const numVal = isNaN(value) ? 0 : Number(value)
            groupedConversionsMap[csvRow[0] + csvHeaders[col]] = numVal
            totalConversionsCount += numVal
          }

          if (col === 1 && withTimestamp) {
            if (!metricsInFile.includes(value)) metricsInFile.push(value)
            if (row < conversionsCount) {
              // csvRow[0]: flyer code, value: metric name
              const key = csvRow[0] + value
              groupedConversionsMap[key] = (groupedConversionsMap[key] || 0) + 1
            }
          }
        })
      })
      const result = await validateConversionsCount(campaignId)
      const { flyerCodesWithLessConversions } = result
      const onContinueUpload3 = () => {
        if (flyerCodesWithLessConversions.length !== 0) {
          treatLessConversionFlyerCodes()
        }
        startRunningValidation()
        validateRule({
          stage: 4,
          campaignId,
          campaignType,
          reportFile,
          callbacks,
          withTimestamp,
          metrics,
          conversionsCount,
          isUSFormat,
          startRunningValidation,
        })
      }
      if (flyerCodesWithLessConversions.length) {
        controller.abort()
        const tableData = flyerCodesWithLessConversions.reduce(
          (acc, flyerCode) => {
            const row = [flyerCode]
            const { dbValues, fileValues } = metrics.reduce(
              ({ dbValues, fileValues }, metric) => {
                const key = flyerCode + metric
                dbValues.push(
                  (groupedConversionsMap[key] | 0) -
                    (updatedConversionsMap[key] | 0),
                )
                fileValues.push(groupedConversionsMap[key] | 0)
                return { dbValues, fileValues }
              },
              { dbValues: [], fileValues: [] },
            )
            row.push(...dbValues, ...fileValues)
            acc.push(row)
            return acc
          },
          [],
        )
        callbacks.onError(
          LESS_CONVERSIONS_THAN_DB,
          onContinueUpload3,
          onCancelUpload,
          { metrics, tableData },
          withTimestamp,
        )
        return
      }
      onContinueUpload3()
      break
    case 4:
      if (withTimestamp) {
        const { invalidRows } = validateTimestamps(datas, isUSFormat)

        if (invalidRows.length) {
          controller.abort()
          const tableData = invalidRows.map(row => [row, ...datas[row]])
          callbacks.onError(INVALID_TIMESTAMP, null, null, {
            csvHeaders,
            tableData,
          })
          return
        }
      }
      if (totalConversionsCount <= conversionsCount) {
        controller.abort()
        callbacks.onError(NO_NEW_CONVERSION)
        return
      }
      const response = await callAuthorizedGraphQLEndpoint(
        uploadConversionLog,
        {
          input: {
            campaignId,
            fileName: reportFile.name,
            reportType: withTimestamp
              ? CONVERSION_REPORT_TYPE.WITH_TIMESTAMP
              : CONVERSION_REPORT_TYPE.WITHOUT_TIMESTAMP,
            status: CONVERSION_LOG_STATUS.SUCCESSED,
            flyersCount: flyerCodes.length,
            conversionsCount: totalConversionsCount - conversionsCount,
          },
        },
      )
      const { id: logId } = (await response.json()).data.uploadConversionLog

      let chunkSize = 0
      let reportData = []
      let metError = false
      let chunkConversionSum = 0
      for (let i = 0, len = datas.length; i < len; i++) {
        if (metError) break
        if (withTimestamp) {
          if (i < conversionsCount) continue
          reportData.push([
            datas[i][0],
            datas[i][1],
            new Date(
              getUNIXTimestamp(datas[i][2], isUSFormat) * 1000,
            ).toISOString(),
          ])
          chunkSize++
        } else {
          const flyerCode = datas[i][0]
          const { arr, sum } = metrics.reduce(
            ({ arr, sum }, metric) => {
              const tmpValue = updatedConversionsMap[flyerCode + metric] | 0
              const value = tmpValue > 0 ? tmpValue : 0
              arr.push(value.toString())
              sum += value
              return { arr, sum }
            },
            { arr: [], sum: 0 },
          )
          if (sum) {
            chunkConversionSum += sum
            reportData.push([flyerCode, ...arr])
            chunkSize++
          }
        }

        if (chunkSize === 200 || i === len - 1 || chunkConversionSum > 2000) {
          try {
            await callAuthorizedGraphQLEndpoint(uploadConversions, {
              input: {
                campaignId,
                csvHeaders,
                data: reportData,
                withTimestamp,
                logId,
              },
            })
            chunkConversionSum = 0
          } catch (e) {
            metError = true
          }

          chunkSize = 0
          reportData = []
        }
      }

      if (metError) {
        callbacks.stopLoading()
        return
      }

      callbacks.onSuccess()
      break
    default:
      break
  }
}
export const addConversionReport = ({
  campaignId,
  reportFile,
  callbacks,
  withTimestamp,
  metrics,
  conversionsCount,
  isUSFormat,
}) => {
  const controller = new AbortController()
  const reader = new FileReader()

  reader.addEventListener('load', async function() {
    const divider = /\r\n|\n/
    const data = Buffer.from(this.result.split(',')[1], 'base64').toString(
      'ascii',
    )

    const dataArr = data.split(divider)
    const headers = dataArr.shift()
    const { csvHeaders, headersLen } = extractCorrectHeaders(headers)

    if (!validateFileStructure(csvHeaders, metrics, withTimestamp)) {
      controller.abort()
      callbacks.onError(WRONG_FILE_STRUCTURE)
      return
    }

    const {
      datas,
      groupedConversionsMap,
      metricsInFile,
      flyerCodes,
      missingValues,
      totalConversionsCount,
    } = extractInfoFromReportFile({
      dataArr,
      headersLen,
      conversionsCount,
      csvHeaders,
      withTimestamp,
    })

    if (missingValues.length) {
      controller.abort()
      const tableData = missingValues.map(({ row }) => [row, ...datas[row]])
      callbacks.onError(MISSING_VALUE, { missingValues, csvHeaders, tableData })
      return
    }

    if (withTimestamp) {
      const nonExistMetrics = validateMetrics(metrics, metricsInFile)
      if (nonExistMetrics.length) {
        controller.abort()
        const tableData = datas.reduce((acc, data, index) => {
          if (!metrics.includes(data[1])) acc.push([index, ...data])
          return acc
        }, [])
        callbacks.onError(NOT_EXIST_METRIC, { csvHeaders, tableData })
        return
      }
    }

    if (!withTimestamp) {
      const duplicatedFlyerCodes = validateFlyerCodesDuplicates(flyerCodes)
      if (duplicatedFlyerCodes.length) {
        controller.abort()
        const tableData = datas.reduce((acc, data, index) => {
          if (duplicatedFlyerCodes.includes(data[0])) acc.push([index, ...data])
          return acc
        }, [])
        callbacks.onError(DUPLICATE_FLYER_CODE, { csvHeaders, tableData })
        return
      }
    }

    const {
      missingFlyerCodes,
      flyerCodesWithLessConversions,
      updatedConversionsMap,
    } = await validateFlyerCodes(campaignId, groupedConversionsMap, flyerCodes)
    if (missingFlyerCodes.length) {
      controller.abort()
      const tableData = datas.reduce((acc, data, index) => {
        if (missingFlyerCodes.includes(data[0])) acc.push([index, ...data])
        return acc
      }, [])
      callbacks.onError(NOT_EXIST_FLYER_CODE, { csvHeaders, tableData })
      return
    }

    if (withTimestamp) {
      const {
        compareOrderResult,
        randomConversions,
      } = await validateConversionsOrder(campaignId, datas, isUSFormat)
      if (compareOrderResult.length) {
        controller.abort()
        const tableData = compareOrderResult.map(({ row }) => {
          const conv = randomConversions.find(item => item.row === row)
          if (!conv) return []
          const { flyerCode, metricName, timestamp } = conv
          return [
            row,
            flyerCode,
            metricName,
            isUSFormat
              ? moment
                  .unix(timestamp / 1000)
                  .utc()
                  .format('MM/DD/YYYY hh:mm:ss')
              : moment
                  .unix(timestamp / 1000)
                  .utc()
                  .format('DD/MM/YYYY HH:mm:ss'),
            ...(datas[row] || ['', '', '']),
          ]
        })
        callbacks.onError(WRONG_CONVERSION_ORDER, {
          csvHeaders,
          tableData,
          compareOrderResult,
        })
        return
      }
    }

    if (flyerCodesWithLessConversions.length) {
      controller.abort()
      const tableData = flyerCodesWithLessConversions.reduce(
        (acc, flyerCode) => {
          const row = [flyerCode]
          const { dbValues, fileValues } = metrics.reduce(
            ({ dbValues, fileValues }, metric) => {
              const key = flyerCode + metric
              dbValues.push(
                (groupedConversionsMap[key] | 0) -
                  (updatedConversionsMap[key] | 0),
              )
              fileValues.push(groupedConversionsMap[key] | 0)
              return { dbValues, fileValues }
            },
            { dbValues: [], fileValues: [] },
          )
          row.push(...dbValues, ...fileValues)
          acc.push(row)
          return acc
        },
        [],
      )
      callbacks.onError(LESS_CONVERSIONS_THAN_DB, { metrics, tableData })
      return
    }

    if (withTimestamp) {
      const { invalidRows } = validateTimestamps(datas, isUSFormat)

      if (invalidRows.length) {
        controller.abort()
        const tableData = invalidRows.map(row => [row, ...datas[row]])
        callbacks.onError(INVALID_TIMESTAMP, { csvHeaders, tableData })
        return
      }
    }
    if (totalConversionsCount <= conversionsCount) {
      controller.abort()
      callbacks.onError(NO_NEW_CONVERSION)
      return
    }

    const response = await callAuthorizedGraphQLEndpoint(uploadConversionLog, {
      input: {
        campaignId,
        fileName: reportFile.name,
        reportType: withTimestamp
          ? CONVERSION_REPORT_TYPE.WITH_TIMESTAMP
          : CONVERSION_REPORT_TYPE.WITHOUT_TIMESTAMP,
        status: CONVERSION_LOG_STATUS.SUCCESSED,
        flyersCount: flyerCodes.length,
        conversionsCount: totalConversionsCount - conversionsCount,
      },
    })
    const { id: logId } = (await response.json()).data.uploadConversionLog

    let chunkSize = 0
    let reportData = []
    let metError = false
    for (let i = 0, len = datas.length; i < len; i++) {
      if (metError) break
      if (withTimestamp) {
        if (i < conversionsCount) continue
        reportData.push([
          datas[i][0],
          datas[i][1],
          new Date(
            getUNIXTimestamp(datas[i][2], isUSFormat) * 1000,
          ).toISOString(),
        ])
        chunkSize++
      } else {
        const flyerCode = datas[i][0]
        const { arr, sum } = metrics.reduce(
          ({ arr, sum }, metric) => {
            const value = updatedConversionsMap[flyerCode + metric] | 0
            arr.push(value.toString())
            sum += value
            return { arr, sum }
          },
          { arr: [], sum: 0 },
        )
        if (sum) {
          reportData.push([flyerCode, ...arr])
          chunkSize++
        }
      }

      if (chunkSize === 200 || i === len - 1) {
        try {
          await callAuthorizedGraphQLEndpoint(uploadConversions, {
            input: {
              campaignId,
              csvHeaders,
              data: reportData,
              withTimestamp,
              logId,
            },
          })
        } catch (e) {
          metError = true
        }

        chunkSize = 0
        reportData = []
      }
    }

    if (metError) {
      callbacks.stopLoading()
      return
    }

    callbacks.onSuccess()
  })

  reader.readAsDataURL(reportFile)
}

export const reportTemplateCSVFileContent = (
  campaignName,
  metrics,
  withTimestamp,
) => {
  if (withTimestamp) {
    const headers = [
      { label: 'Flyer code', key: 'flyerCode' },
      { label: 'Metric', key: 'metric' },
      { label: 'Timestamp', key: 'timestamp' },
      { label: '', key: 'space' },
      { label: '', key: 'campaignMetrics' },
      { label: '', key: 'flyerCodeEx' },
      { label: '', key: 'metricEx' },
      { label: '', key: 'timestampEx' },
    ]
    const tempRow = {}
    headers.forEach(({ key }) => (tempRow[key] = ''))

    const data = []
    for (let i = 0; i < metrics.length + 3; i++) data.push({ ...tempRow })
    data[1].campaignMetrics = 'Campaign metrics'
    data[1].flyerCodeEx = 'Example'
    data[2].flyerCodeEx = 'flyer code'
    data[2].metricEx = 'metric'
    data[2].timestampEx = 'timestamp'
    metrics.forEach((metric, index) => {
      data[index + 2].campaignMetrics = metric
      data[index + 3].flyerCodeEx = `OPZ00${index + 1}`
      data[index + 3].metricEx = metric
      data[index + 3].timestampEx = '01/01/2022 00:00:00'
    })

    const tempCSVFile = {
      data,
      headers,
      filename: `${campaignName}_conversions_template.csv`,
    }
    return tempCSVFile
  } else {
    const headers = [{ label: 'Flyer code', key: 'flyerCode' }]
    metrics.forEach(metric => headers.push({ label: metric, key: metric }))
    headers.push({ label: '', key: 'space' })
    headers.push({ label: '', key: 'campaignMetrics' })
    headers.push({ label: '', key: 'flyerCodeEx' })
    metrics.forEach(metric => headers.push({ label: '', key: `${metric}Ex` }))

    const tempRow = {}
    headers.forEach(({ key }) => (tempRow[key] = ''))

    const data = []
    const len = metrics.length
    for (let i = 0; i < len + 3; i++) data.push({ ...tempRow })
    data[1].campaignMetrics = 'Campaign metrics'
    data[1].flyerCodeEx = 'Example'
    data[2].flyerCodeEx = 'flyer code'

    metrics.forEach((metric, index) => {
      data[index + 2].campaignMetrics = metric
      data[2][`${metric}Ex`] = metric
      data[3][`${metric}Ex`] = 10
      if (len > 1) data[4][`${metric}Ex`] = 20
    })
    if (len) data[3].flyerCodeEx = 'OPZ001'
    if (len > 1) data[4].flyerCodeEx = 'OPZ002'

    const tempCSVFile = {
      data,
      headers,
      filename: `${campaignName}_conversions_template.csv`,
    }
    return tempCSVFile
  }
}

export const invalidFileStructureTableContent = (
  withTimestamp,
  { metrics, fileHeader, fileData },
) => {
  if (fileData.length < 3) {
    let count = fileData.length
    while (count < 3) {
      fileData.push('')
      count++
    }
  }
  const blankArray = ['', '', '', '', '']
  const headerLength = fileHeader.length
  const metricsLength = metrics.length
  if (withTimestamp) {
    const size = 3
    const sampleRows = [
      ['CODE001', 'Signups', '01/01/2022 12:15:35'],
      ['CODE001', 'Purchase', '01/01/2022 13:43:00'],
      ['CODE002', 'Signups', '01/02/2022 07:23:51'],
    ]
    const headers = [
      { label: 'Expected', span: size },
      { label: 'Actual', span: size },
    ]
    const rows = []
    const firstRow = ['Flyer code', 'Metric', 'Timestamp', ...fileHeader].map(
      (label, index, arr) => {
        if (index < 3) {
          return {
            label,
            isHighlight: false,
          }
        } else {
          const highlight = !(arr.slice(0, 3)[index - 3] === label)
          return {
            label,
            isHighlight: highlight,
          }
        }
      },
    )
    if (headerLength < 3) {
      firstRow.push(
        ...blankArray
          .slice(0, 3 - headerLength)
          .map(label => ({ label, isHighlight: true })),
      )
    }

    rows.push(firstRow)
    rows.push(
      ...fileData.map((data, index) => {
        const line = []
        line.push(...sampleRows[index])
        const tempFileRow = data.split(',').slice(0, size)
        let count = tempFileRow.length
        while (count < size) {
          tempFileRow.push('')
          count++
        }
        line.push(...tempFileRow)
        const temp = line.map(label => ({
          label,
          isHighlight: false,
        }))
        return [...temp]
      }),
    )
    return { headers, rows }
  } else {
    const size = metricsLength + 1
    const headers = [
      { label: 'Expected', span: size },
      { label: 'Actual', span: size },
    ]
    const rows = []
    const firstRow = ['Flyer code', ...metrics, ...fileHeader].map(
      (label, index, arr) => {
        if (index < metrics.length + 1) {
          return {
            label,
            isHighlight: false,
          }
        } else {
          const highlight = !(
            arr.slice(0, metricsLength + 1)[index - metricsLength - 1] === label
          )
          return {
            label,
            isHighlight: highlight,
          }
        }
      },
    )
    if (headerLength < metricsLength + 1) {
      firstRow.push(
        ...blankArray
          .slice(0, metricsLength + 1 - headerLength)
          .map(label => ({ label, isHighlight: true })),
      )
    }
    rows.push(firstRow)
    const sampleRows = [
      ['CODE001', '6', '4', '5', '4', '5'],
      ['CODE002', '4', '3', '4', '5', '6'],
      ['CODE003', '13', '10', '4', '5', '6'],
    ]

    rows.push(
      ...fileData.map((data, index) => {
        const line = []
        line.push(...sampleRows[index].slice(0, metricsLength + 1))
        const tempFileRow = data.split(',').slice(0, size)
        let count = tempFileRow.length
        while (count < size) {
          tempFileRow.push('')
          count++
        }
        line.push(...tempFileRow)
        const temp = line.map(label => ({
          label,
          isHighlight: false,
        }))
        return [...temp]
      }),
    )
    return { headers, rows }
  }
}

export const errorReportCSVFileContent = ({ csvHeaders, tableData }) => {
  const headers = ['Row number', ...csvHeaders]
  let tempTable = tableData.reduce((acc, row) => {
    let line = [...row]
    line[0] = Number(line[0]) + 2
    acc.push(line)
    return acc
  }, [])
  const tempCSVFile = {
    data: [headers, ...tempTable],
    filename: ERROR_CSV_FILE_NAME,
  }
  return tempCSVFile
}

export const lessConversionsCSVFileContent = ({ metrics, tableData }) => {
  const headers = ['Flyer code', ...metrics, ...metrics]

  const tempCSVFile = {
    data: [headers, ...tableData],
    filename: ERROR_CSV_FILE_NAME,
  }
  return tempCSVFile
}

export const missingValuesTableContent = ({
  missingValues,
  csvHeaders,
  tableData,
}) => {
  const headers = ['Row number', ...csvHeaders].map(label => ({
    label,
    span: 1,
  }))
  const rows = []
  tableData.slice(0, SHOW_ERROR_NUMBER).forEach((data, index) => {
    const { pos } = missingValues[index]
    const line = data.map(label => ({ label, isHighlight: false }))
    line[0].label = Number(line[0].label) + 2
    pos.forEach(idx => {
      line[idx + 1] = { label: '', isHighlight: true }
    })
    rows.push([...line])
  })

  return { headers, rows }
}

export const nonExistMetricsTableContent = ({ csvHeaders, tableData }) => {
  const headers = ['Row number', ...csvHeaders].map(label => ({
    label,
    span: 1,
  }))
  const rows = []
  tableData.slice(0, SHOW_ERROR_NUMBER).forEach(data => {
    const line = data.map(label => ({ label, isHighlight: false }))
    line[2].isHighlight = true
    line[0].label = Number(line[0].label) + 2
    rows.push([...line])
  })

  return { headers, rows }
}

export const wrongFlyerCodesTableContent = ({ csvHeaders, tableData }) => {
  const headers = ['Row number', ...csvHeaders].map(label => ({
    label,
    span: 1,
  }))
  const rows = []
  tableData.slice(0, SHOW_ERROR_NUMBER).forEach(data => {
    const line = data.map(label => ({ label, isHighlight: false }))
    line[1].isHighlight = true
    line[0].label = 2 + Number(line[0].label)
    rows.push([...line])
  })

  return { headers, rows }
}

export const wrongConversionOrderTableContent = ({
  csvHeaders,
  tableData,
  compareOrderResult,
}) => {
  const headers = [
    { label: 'Row number', span: 1 },
    { label: 'Database', span: 3 },
    { label: 'file', span: 3 },
  ]
  const rows = []
  const firstRow = ['', ...csvHeaders, ...csvHeaders].map(label => ({
    label,
    isHighlight: false,
  }))
  rows.push(firstRow)
  rows.push(
    ...tableData.map((data, index) => {
      const line = data.map(label => ({ label, isHighlight: false }))
      line[0].label = Number(line[0].label) + 2
      const { misMatched } = compareOrderResult[index]
      misMatched.forEach(pos => {
        line[1 + pos].isHighlight = true
        line[4 + pos].isHighlight = true
      })
      return [...line]
    }),
  )

  return { headers, rows }
}

export const lessConversionsTableContent = ({ metrics, tableData }) => {
  const metricsLen = metrics.length
  const headers = [
    { label: '', span: 1 },
    { label: 'Database', span: metrics.length },
    { label: 'File', span: metrics.length },
  ]
  const rows = []
  const firstRow = ['Flyer code', ...metrics, ...metrics].map(label => ({
    label,
    isHighlight: false,
  }))
  rows.push(firstRow)
  rows.push(
    ...tableData.slice(0, SHOW_ERROR_NUMBER).map(data => {
      const line = data.map(label => ({ label, isHighlight: false }))
      for (let i = 0; i < metricsLen; i++) {
        if (data[1 + i] > data[1 + metricsLen + i]) {
          line[1 + i].isHighlight = true
          line[1 + metricsLen + i].isHighlight = true
        }
      }
      return [...line]
    }),
  )

  return { headers, rows }
}

export const invalidTimestampTableContent = ({ csvHeaders, tableData }) => {
  const headersLen = csvHeaders.length
  const headers = ['Row number', ...csvHeaders].map(label => ({
    label,
    span: 1,
  }))
  const rows = []
  tableData.slice(0, SHOW_ERROR_NUMBER).forEach(data => {
    const line = data.map(label => ({ label, isHighlight: false }))
    line[0].label = Number(line[0].label) + 2
    line[headersLen].isHighlight = true
    rows.push([...line])
  })

  return { headers, rows }
}
